/**
 * Eight orbs to a ring, three rings.
 *
 * The outer ring used to absorb everything above eight, so a full orbit crammed
 * sixteen spheres onto one circle and they merged into a band. Three rings of eight
 * come to twenty-four, exactly the ceiling `maxOrbs` allows, so a full orbit reads
 * as three rings rather than one blur.
 *
 * These live here rather than in `orbit.ts` because the collision radius needs them
 * too, and `orbit.ts` already depends on this module — the other direction would be
 * a cycle.
 */
export const RING_SLOTS = 8;
export const ORBIT_RINGS = 3;
export const MAX_ORBIT_SLOTS = RING_SLOTS * ORBIT_RINGS;

export type ArenaTheme = "deep-space" | "void-purple" | "aurora";

export type OrbitLayout = {
  firstOrbitRadius: number;
  secondOrbitRadius: number;
  thirdOrbitRadius: number;
};

export type GameSettings = {
  matchDuration: number;
  coreRadius: number;
  firstOrbitRadius: number;
  secondOrbitRadius: number;
  thirdOrbitRadius: number;
  orbRadius: number;
  playerColor: string;
  theme: ArenaTheme;
  maxOrbs: number;
  ownEnergyScore: number;
  enemyEnergyScore: number;
  stealScore: number;
  /** The first ring's wait. Deeper rings wait longer; see `surgeWait`. */
  surgeCooldown: number;
  shieldCaptureScore: number;
  fireInterval: number;
  shotRange: number;
  shotSpeed: number;
  returnSpeed: number;
  botSkill: number;
  boostMultiplier: number;
  boostDuration: number;
  boostTurnControl: number;
  stationInterval: number;
  stationActiveDuration: number;
  stationRadius: number;
  stationBonus: number;
};

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  matchDuration: 90,
  coreRadius: 25,
  firstOrbitRadius: 55,
  secondOrbitRadius: 78,
  thirdOrbitRadius: 101,
  orbRadius: 7.5,
  playerColor: "#36d8ff",
  theme: "deep-space",
  maxOrbs: 16,
  ownEnergyScore: 10,
  enemyEnergyScore: 10,
  stealScore: 90,
  surgeCooldown: 6,
  shieldCaptureScore: 65,
  fireInterval: 0.5,
  shotRange: 480,
  shotSpeed: 430,
  returnSpeed: 560,
  botSkill: 0.75,
  boostMultiplier: 1.5,
  boostDuration: 1,
  boostTurnControl: 0.85,
  stationInterval: 30,
  stationActiveDuration: 8,
  stationRadius: 62,
  stationBonus: 8,
};

export const THEME_OPTIONS: ReadonlyArray<{ value: ArenaTheme; label: string }> = [
  { value: "deep-space", label: "Derin Uzay" },
  { value: "void-purple", label: "Mor Boşluk" },
  { value: "aurora", label: "Aurora" },
];

export const ARENA_PALETTES: Record<ArenaTheme, { center: string; middle: string; edge: string; grid: string }> = {
  "deep-space": { center: "#101d51", middle: "#071233", edge: "#030817", grid: "#6385cb" },
  "void-purple": { center: "#281452", middle: "#11092e", edge: "#050410", grid: "#9a6be8" },
  aurora: { center: "#073d4b", middle: "#06253a", edge: "#020b18", grid: "#53c8bc" },
};

/**
 * Only these are safe for a client to own. Everything else — including the orbit
 * radii, which look cosmetic but feed `getCollisionRadius` and therefore the
 * hitbox — decides the outcome of a fight and must come from the server once real
 * players share an arena.
 */
export const PRESENTATION_KEYS = ["playerColor", "theme"] as const;

export type PresentationKey = (typeof PRESENTATION_KEYS)[number];
export type PresentationSettings = Pick<GameSettings, PresentationKey>;
export type BalanceSettings = Omit<GameSettings, PresentationKey>;

/**
 * Balance belongs to the arena, not the player. Every value outside
 * `PRESENTATION_KEYS` changes the outcome of a fight for everyone in the room, so it
 * is set from the operator panel and pushed to clients — never edited locally.
 */
export const BALANCE_IS_SERVER_OWNED = true;

export function toPresentationSettings(settings: GameSettings): PresentationSettings {
  return { playerColor: settings.playerColor, theme: settings.theme };
}

export function toBalanceSettings(settings: GameSettings): BalanceSettings {
  const balance = { ...settings } as GameSettings & Partial<PresentationSettings>;
  for (const key of PRESENTATION_KEYS) delete balance[key];
  return balance as BalanceSettings;
}

export function isPresentationKey(key: keyof GameSettings): key is PresentationKey {
  return (PRESENTATION_KEYS as readonly string[]).includes(key);
}

/**
 * Server balance always wins. Only the presentation keys are read off the client
 * payload — spreading it wholesale would let a client smuggle `shotRange` or
 * `fireInterval` past the server's values.
 */
export function mergeSettings(balance: BalanceSettings, presentation: Partial<GameSettings>): GameSettings {
  const claimed: Partial<PresentationSettings> = {};
  for (const key of PRESENTATION_KEYS) {
    if (presentation[key] !== undefined) claimed[key] = presentation[key] as never;
  }
  return normalizeSettings({ ...balance, ...claimed });
}

function safeNumber(value: unknown, fallback: number, min: number, max: number) {
  return typeof value === "number" && Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;
}

export function normalizeSettings(value: unknown): GameSettings {
  const input = value && typeof value === "object" ? value as Partial<GameSettings> : {};
  const legacyStationBonus = value && typeof value === "object" ? (value as Record<string, unknown>).stationRefillCap : undefined;
  const theme = typeof input.theme === "string" && input.theme in ARENA_PALETTES ? input.theme as ArenaTheme : DEFAULT_GAME_SETTINGS.theme;
  const playerColor = typeof input.playerColor === "string" && /^#[0-9a-f]{6}$/i.test(input.playerColor) ? input.playerColor : DEFAULT_GAME_SETTINGS.playerColor;
  return withRingsInOrder({
    // 0 is "no clock", a value of its own rather than a length; anything else is
    // a real match length and clamps into range.
    matchDuration: Number(input.matchDuration) === 0
      ? 0
      : safeNumber(input.matchDuration, DEFAULT_GAME_SETTINGS.matchDuration, 30, 300),
    coreRadius: safeNumber(input.coreRadius, DEFAULT_GAME_SETTINGS.coreRadius, 18, 34),
    firstOrbitRadius: safeNumber(input.firstOrbitRadius, DEFAULT_GAME_SETTINGS.firstOrbitRadius, 42, 72),
    secondOrbitRadius: safeNumber(input.secondOrbitRadius, DEFAULT_GAME_SETTINGS.secondOrbitRadius, 64, 98),
    thirdOrbitRadius: safeNumber(input.thirdOrbitRadius, DEFAULT_GAME_SETTINGS.thirdOrbitRadius, 88, 130),
    orbRadius: safeNumber(input.orbRadius, DEFAULT_GAME_SETTINGS.orbRadius, 5, 11),
    playerColor,
    theme,
    maxOrbs: safeNumber(input.maxOrbs, DEFAULT_GAME_SETTINGS.maxOrbs, 8, 24),
    ownEnergyScore: safeNumber(input.ownEnergyScore, DEFAULT_GAME_SETTINGS.ownEnergyScore, 0, 50),
    enemyEnergyScore: safeNumber(input.enemyEnergyScore, DEFAULT_GAME_SETTINGS.enemyEnergyScore, 0, 100),
    stealScore: safeNumber(input.stealScore, DEFAULT_GAME_SETTINGS.stealScore, 40, 180),
    surgeCooldown: safeNumber(input.surgeCooldown, DEFAULT_GAME_SETTINGS.surgeCooldown, 0, 20),
    shieldCaptureScore: safeNumber(input.shieldCaptureScore, DEFAULT_GAME_SETTINGS.shieldCaptureScore, 20, 120),
    fireInterval: safeNumber(input.fireInterval, DEFAULT_GAME_SETTINGS.fireInterval, 0.2, 1.5),
    shotRange: safeNumber(input.shotRange, DEFAULT_GAME_SETTINGS.shotRange, 300, 700),
    shotSpeed: safeNumber(input.shotSpeed, DEFAULT_GAME_SETTINGS.shotSpeed, 280, 650),
    returnSpeed: safeNumber(input.returnSpeed, DEFAULT_GAME_SETTINGS.returnSpeed, 350, 800),
    botSkill: safeNumber(input.botSkill, DEFAULT_GAME_SETTINGS.botSkill, 0.2, 1),
    boostMultiplier: safeNumber(input.boostMultiplier, DEFAULT_GAME_SETTINGS.boostMultiplier, 1.2, 2),
    boostDuration: safeNumber(input.boostDuration, DEFAULT_GAME_SETTINGS.boostDuration, 0.5, 2),
    boostTurnControl: safeNumber(input.boostTurnControl, DEFAULT_GAME_SETTINGS.boostTurnControl, 0.5, 1),
    stationInterval: safeNumber(input.stationInterval, DEFAULT_GAME_SETTINGS.stationInterval, 15, 120),
    stationActiveDuration: safeNumber(input.stationActiveDuration, DEFAULT_GAME_SETTINGS.stationActiveDuration, 4, 20),
    stationRadius: safeNumber(input.stationRadius, DEFAULT_GAME_SETTINGS.stationRadius, 45, 90),
    stationBonus: safeNumber(input.stationBonus ?? legacyStationBonus, DEFAULT_GAME_SETTINGS.stationBonus, 1, 8),
  });
}

/**
 * The three rings have overlapping ranges, so an operator can order them wrongly —
 * a second ring inside the first draws orbs on top of each other and makes the
 * collision radius shrink as a core grows. Each ring is pushed out to clear the one
 * inside it rather than the setting being refused: a slider that silently snaps back
 * is harder to understand than one that nudges.
 */
function withRingsInOrder(settings: GameSettings): GameSettings {
  const gap = settings.orbRadius * 2 + 2;
  const second = Math.max(settings.secondOrbitRadius, settings.firstOrbitRadius + gap);
  return {
    ...settings,
    secondOrbitRadius: second,
    thirdOrbitRadius: Math.max(settings.thirdOrbitRadius, second + gap),
  };
}

/**
 * The deepest ring the orbit has filled: 8-15 is one, 16-23 is two, 24 is three.
 *
 * Not an exact count. Requiring exactly eight punished the ninth orb — and orbs are
 * picked up by walking near them, so losing the charge was not a decision anybody
 * made. Growing should never take something away.
 *
 * It also means a surge steps *down* as it is spent: fire at twenty-four and you
 * are at twenty-three, so the next one comes back at ring two.
 */
export function surgeTier(orbs: number) {
  if (orbs < RING_SLOTS) return 0;
  return Math.min(ORBIT_RINGS, Math.floor(orbs / RING_SLOTS));
}

/** What a charged shot from that ring takes: 2, 3, 4. */
export function surgeSteal(tier: number) {
  return tier + 1;
}

/**
 * How long that ring waits before it can be spent again: 6, 9, 12 at the default.
 *
 * Growing has to cost something. Without this a deeper ring is strictly better —
 * more taken, same wait — and there is no reason to ever stay small. Half again per
 * ring makes it a trade: hit harder, hit rarer, and hanging back at one ring to
 * fire twice as often is a way to play rather than a mistake.
 */
export function surgeWait(tier: number, settings: Pick<GameSettings, "surgeCooldown">) {
  return settings.surgeCooldown * (1 + Math.max(0, tier - 1) * 0.5);
}

/**
 * The ring this core could spend right now: a closed ring and no wait left. Read
 * rather than remembered, so nothing can drift out of step with the orb count.
 * It lives here rather than in step.ts so the bots can consult it without the two
 * modules importing each other.
 */
export function chargeOf(participant: { orbs: number; surgeCooldown: number }) {
  return participant.surgeCooldown > 0 ? 0 : surgeTier(participant.orbs);
}

/** The ring a given orb sits on, innermost first. */
export function orbitRingOf(index: number) {
  return Math.min(ORBIT_RINGS - 1, Math.floor(index / RING_SLOTS));
}

export function orbitRingRadius(ring: number, settings: OrbitLayout) {
  if (ring >= 2) return settings.thirdOrbitRadius;
  if (ring === 1) return settings.secondOrbitRadius;
  return settings.firstOrbitRadius;
}

export function getCollisionRadius(orbs: number, settings: GameSettings) {
  if (orbs <= 0) return settings.coreRadius + 3;
  // The ring the outermost orb is on: hitting an orbit means reaching the spheres
  // you can see, and a third ring has to widen the target as the second one does.
  return orbitRingRadius(orbitRingOf(orbs - 1), settings) + settings.orbRadius;
}

export function getShieldRadius(settings: GameSettings) {
  return settings.thirdOrbitRadius + settings.orbRadius + 16;
}
